import React, { useEffect, useRef, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import { TablePagination } from "@material-ui/core";

import DeleteIcon from "@material-ui/icons/Delete";

import { useModuleGroupUpdate } from "../moduleGroupsService";
import RemoveModuleDialog from "./RemoveModuleDialog";
import { notBlank } from "../../util/ext";
import { equals, reject, sortBy, compose, toLower, prop, reverse } from "ramda";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    border: "white",
    borderRadius: "20px",
  },

  container: {
    height: "calc(100vh - 350px)",
  },

  row: {
    backgroundColor: "white",
  },

  contentCell: {
    width: "33%",
  },

  title: {
    color: "#127DA9",
    width: "33%",
  },

  organization: {
    width: "25%",
  },

  cell: {
    minWidth: 30,
    padding: 0,
  },

  cellRoot: {
    border: "none",
    padding: "8px 0",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },

  deleteCell: {
    width: "10%",
  },

  headerCell: {
    backgroundColor: "white",
  },

  popoverPaper: {
    borderRadius: "20px",
    padding: "5px 12px",
    margin: "0",
    boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.15)",
  },

  popoverText: {
    fontStyle: "italic",
    fontSize: "10px",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    border: "white",
    borderRadius: "20px",
  },
}));

const ModuleGroupTable = props => {
  const {
    initialModuleGroup,
    page,
    rowsPerPage,
    onPageChange,
    onModuleRemove,
    total,
    onScrollVisible,
  } = props;

  const classes = useStyles();

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, tableContainerEl]);

  const [open, setOpen] = useState(false);
  const [activeModule, setActiveModule] = useState({});
  const [moduleGroup, setModuleGroup] = useState({
    modules: [],
    filteredModules: [],
  });

  useEffect(() => {
    setOrderedList(initialModuleGroup.filteredModules);
  }, [initialModuleGroup.filteredModules]);

  useEffect(() => {
    setModuleGroup(initialModuleGroup);
  }, [initialModuleGroup]);

  const updateModuleGroup = useModuleGroupUpdate();

  const handleModuleRemove = useCallback(async () => {
    try {
      const newModules = reject(
        module => equals(module.moduleId, activeModule.moduleId),
        moduleGroup.modules,
      );

      moduleGroup.modules = newModules;
      await updateModuleGroup.mutateAsync({
        resourceIds: { id: moduleGroup.id },
        data: moduleGroup,
      });
      onModuleRemove();
    } catch (e) {
      console.log(e);
    } finally {
      setOpen(false);
    }
  }, [updateModuleGroup, moduleGroup, activeModule.moduleId, onModuleRemove]);

  const handleClickOpen = activeModule => {
    setActiveModule(activeModule);
    setOpen(true);
  };

  const handleClickClose = () => {
    setActiveModule({});
    setOpen(false);
  };

  //-- Sorting Modules
  const [orderedList, setOrderedList] = useState(
    initialModuleGroup.filteredModules,
  );
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("");
  const handleRequestSort = orderBy => {
    const newOrder = equals(order, "asc") ? "desc" : "asc";
    setOrder(newOrder);

    const newOrderedList = comparator(newOrder)(orderBy)(orderedList);
    setOrderBy(orderBy);
    setOrderedList(newOrderedList);
  };

  const comparator = order => orderBy => {
    return equals(order, "asc") ? sortByAsc(orderBy) : sortByDesc(orderBy);
  };

  const sortByAsc = orderBy => sortBy(compose(toLower, prop(orderBy)));
  const sortByDesc = orderBy =>
    compose(reverse, sortBy(compose(toLower, prop(orderBy))));

  return (
    <Paper elevation={0} className={classes.root}>
      <RemoveModuleDialog
        module={activeModule}
        open={open}
        handleClickClose={handleClickClose}
        handleModuleRemove={handleModuleRemove}
      />
      <TableContainer ref={tableContainerEl} className={classes.container}>
        <Table stickyHeader aria-label="Module Group Modules">
          <TableHead>
            <TableRow>
              <TableCell
                key="module-id"
                align="left"
                classes={{
                  root: classes.contentCell,
                  stickyHeader: classes.row,
                }}
                style={{ paddingLeft: "30px" }}
              >
                Module ID
              </TableCell>
              <TableCell
                key="module-name"
                align="left"
                classes={{ root: classes.cell, stickyHeader: classes.row }}
              >
                <TableSortLabel
                  active={orderBy === "moduleName"}
                  direction={order}
                  onClick={() => handleRequestSort("moduleName")}
                >
                  Module Name
                  {orderBy === "moduleName" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell
                className={classes.deleteCell}
                classes={{ root: classes.cell, stickyHeader: classes.row }}
              >
                Remove
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderedList
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(module => (
                <TableRow
                  key={module.moduleId}
                  hover
                  tabIndex={-1}
                  classes={{ root: classes.row }}
                >
                  <TableCell
                    key={"id:" + module.moduleId}
                    align="left"
                    className={classes.title}
                    classes={{ root: classes.cellRoot }}
                    style={{ paddingLeft: "30px" }}
                  >
                    {/* <Link
                              to={`${url}/${module.moduleId}`}
                              style={{ color: "#127DA9", fontWeight: "400" }}
                            > */}
                    {module.moduleId}
                    {/* </Link> */}
                  </TableCell>

                  <TableCell
                    key={"module-name:" + module.moduleId}
                    align="left"
                    className={classes.organization}
                    classes={{ root: classes.cellRoot }}
                  >
                    {module.moduleName}
                  </TableCell>
                  <TableCell
                    key={"module-remove:" + module.moduleId}
                    classes={{ root: classes.cellRoot }}
                  >
                    <IconButton
                      aria-label="remove module"
                      onClick={() => handleClickOpen(module)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: classes.pagination,
        }}
        rowsPerPageOptions={[rowsPerPage]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={(event, page) => onPageChange(event)}
      />
    </Paper>
  );
};

export default ModuleGroupTable;
